import React, { ReactElement, useCallback, useMemo } from 'react'
import styled, { css } from 'styled-components'
import { isEqual, kebabCase } from 'lodash'
import { Colors, Divider } from '@blueprintjs/core'
import { Atom, useAtom } from 'klyva'

import { ReorderableList, protrudeLeft, RadioDropdown, Box } from '..'
import { OrderOptionsConfig } from './types'
import getReorder from './get-reorder'
import { ComputeLiProps } from '../reorderable-list'

const StyledOrderColumnsPane = styled.div`
  overflow: auto;
  max-height: 450px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  ol {
    display: flex;
    flex-wrap: wrap;
    height: 100%;
    min-height: min-content;
    max-width: 420px;
    overflow: auto;
    padding-bottom: 20px;
    margin-top: 10px;
    > li {
      padding: 5px;
      margin: 2px;
      cursor: move;
    }
    > li.dnd-is-entered {
      ${() => css`
        animation: ${protrudeLeft} 1s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
      `}
    }
    > li.dnd-is-dragged {
      background-color: ${({ theme }) => {
        return theme.darkMode ? Colors.DARK_GRAY2 : Colors.LIGHT_GRAY2
      }};
    }
  }
`

const OrderColumnsPane = <T extends string, OrderOptionKey extends string>({
  renderColumnLabel,
  enabledColumnIdsAtom,
  orderOptionsConfig,
  currentOrderOptionAtom,
}: {
  renderColumnLabel: (id: T) => ReactElement | null
  enabledColumnIdsAtom: Atom<T[]>
  orderOptionsConfig: OrderOptionsConfig<T, OrderOptionKey>
  currentOrderOptionAtom: Atom<OrderOptionKey | 'custom'>
}) => {
  const [currentOrderOption, setCurrentOrderOption] = useAtom(
    currentOrderOptionAtom,
  )
  const orderOptionKeys = useMemo(
    () => Object.keys(orderOptionsConfig) as OrderOptionKey[],
    [orderOptionsConfig],
  )
  const reorder = useMemo(
    () => getReorder(orderOptionsConfig, currentOrderOption),
    [orderOptionsConfig, currentOrderOption],
  )

  const [enabledColumnIds, setEnabledColumnIds] = useAtom(enabledColumnIdsAtom)
  const getId = (id: T): string => id
  const getElemId = (elem: HTMLElement | undefined) =>
    elem?.getAttribute('data-id') ?? undefined
  const computeLiProps: ComputeLiProps<T> = (id, dragNDropReturn) => ({
    'data-id': id,
    className: (['isDragged', 'isEntered', 'isEnterable'] as const)
      .filter((key) => dragNDropReturn[key] === true)
      .map((str) => `dnd-${kebabCase(str)}`)
      .join(' '),
  })
  const setItems = useCallback(
    (newItems: T[]) => {
      if (isEqual(enabledColumnIds, newItems)) {
        return
      }
      if (
        currentOrderOption !== 'custom' &&
        !isEqual(newItems, reorder(newItems))
      ) {
        setCurrentOrderOption(() => 'custom' as OrderOptionKey)
      }
      setEnabledColumnIds(newItems)
    },
    [
      enabledColumnIds,
      setEnabledColumnIds,
      reorder,
      currentOrderOption,
      setCurrentOrderOption,
    ],
  )
  return (
    <Box>
      <Box flexDir="row" mb={2}>
        <RadioDropdown
          tooltipContent={
            <>
              Click to see alternative ordering shortcuts
              <br />
              {` Drag & drop list items to switch to a ${
                currentOrderOption && currentOrderOption !== 'custom'
                  ? ''
                  : 'different '
              }custom
                ordering`}
            </>
          }
          options={orderOptionKeys}
          selectedOption={
            currentOrderOption === 'custom' ? undefined : currentOrderOption
          }
          setSelectedOption={(
            orderOptionKey: OrderOptionKey | 'custom' | undefined,
          ) => {
            orderOptionKey = orderOptionKey ?? 'custom'
            const newReorder = getReorder(orderOptionsConfig, orderOptionKey)
            setEnabledColumnIds(() => newReorder(enabledColumnIds))
            setCurrentOrderOption(() => orderOptionKey ?? 'custom')
          }}
          getOptionText={(orderOptionKey, selected) => {
            const config =
              !orderOptionKey || orderOptionKey === 'custom'
                ? undefined
                : orderOptionsConfig[
                    orderOptionKey as Exclude<OrderOptionKey, 'custom'>
                  ]
            if (!config) {
              return selected ? 'Using Custom Ordering' : 'Use Custom Ordering'
            } else {
              return (
                (selected ? config.selectedLabel : undefined) ?? config.label
              )
            }
          }}
          getDisabled={(orderOptionKey) =>
            orderOptionKey === currentOrderOption
          }
        />
      </Box>
      <Divider />
      <StyledOrderColumnsPane>
        <ReorderableList<T>
          listId="toggle-menu"
          items={enabledColumnIds}
          setItems={setItems}
          getId={getId}
          getElemId={getElemId}
          computeLiProps={computeLiProps}
          renderItem={renderColumnLabel}
        />
      </StyledOrderColumnsPane>
    </Box>
  )
}

export default OrderColumnsPane
